'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { QRCode } from 'react-qrcode-logo';



import { useAuth } from '@r123/auth';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import Image from 'next/image';



import DownloadIcon from '@/app/_components/Icon/DownloadIcon';
import ShareIcon from '@/app/_components/Icon/ShareIcon';
import AnimatedSpinner from '@/app/_components/Loading/AnimatedSpinner';
import Modal from '@/app/_components/Modal/Modal';
import ReactPortalComponent from '@/app/_components/Portal/Portal';
import { useEffectCallOnce } from '@/app/_hooks/useEffectCallOnce';
import { useTheme } from '@/app/_providers/ThemeProvider';



import { validImage } from '../../_helper/fetchImage';
import { useAgent } from '../../_provider/AgentProvider';
import CardBack from '../IdentityCard/CardBack';
import CardFront from '../IdentityCard/CardFront';


type ModalQRCodeAgentProps = {
    show: boolean;
    isValidImage?: boolean;
    onClose: () => void;
    onShare: () => void;
};

const buttonActionStyle = {
    default:
        'bg-white hover:bg-blue500 border border-solid border-blue500 text-blue500 hover:text-white transition-100',
    blue: 'bg-white hover:bg-blue500 border border-solid border-blue500 text-blue500 hover:text-white  transition-100',
    green: 'bg-white hover:bg-green500 border border-solid border-green500 text-green500 hover:text-white  transition-100',
    dark: 'bg-white hover:bg-dark700 border border-solid border-dark700 text-dark700 hover:text-white  transition-100',
};

const ModalQRCodeAgent = ({
    show,
    onClose,
    onShare,
}: ModalQRCodeAgentProps) => {
    const { authProfile } = useAuth();
    const qrCodeRef = useRef<QRCode>(null);
    const [fullUrl, setFullUrl] = useState('');

    const { theme } = useTheme();
    const cardFrontRef = useRef<HTMLDivElement>(null);
    const cardBackRef = useRef<HTMLDivElement>(null);
    const { name, profilePicture, id: agentId } = useAgent();

    const [isValidImage, setValidImage] = useState(false);
    const [imageFront, setImageFront] = useState('');
    const [imageBack, setImageBack] = useState('');

    const isSelf = useMemo(() => {
        return authProfile?.origin_id === agentId;
    }, [authProfile]);

    useEffectCallOnce(() => {
        if (window)
            setFullUrl(
                `${window.location.href.split('?')[0]}?utm_medium=share&utm_source=qr`,
            );
    });

    useEffect(() => {
        if (show) {
            setTimeout(() => {
                checkValidImage();
            }, 0);
        }
    }, [show]);

    const checkValidImage = async () => {
        const image = await validImage(profilePicture || '');
        setValidImage(!!image);
        setTimeout(() => {
            generateComponentToImage();
        }, 1000);
    };

    const handleDownload = () => {
        qrCodeRef?.current?.download('webp', `${name}-QR`);
    };

    const handleDownloadZip = async () => {
        const zip = new JSZip();

        const base64ToBlob = (base64: string) => {
            const byteCharacters = atob(base64);
            const byteArrays = [];
            for (let i = 0; i < byteCharacters.length; i += 512) {
                const slice = byteCharacters.slice(i, i + 512);
                const byteNumbers = new Array(slice.length);
                for (let j = 0; j < slice.length; j++) {
                    byteNumbers[j] = slice.charCodeAt(j);
                }
                byteArrays.push(new Uint8Array(byteNumbers));
            }
            return new Blob(byteArrays, { type: 'application/octet-stream' });
        };

        [
            { type: 'front', value: imageFront },
            { type: 'back', value: imageBack },
        ].forEach((image) => {
            const base64Data = image.value.split(',')[1];
            zip.file(
                `${name?.split(' ')?.join('-')?.toLowerCase()}-card-${image.type}.png`,
                base64ToBlob(base64Data),
                { base64: true },
            );
        });

        zip.generateAsync({ type: 'blob' }).then((content) => {
            const link = document.createElement('a');
            link.href = URL.createObjectURL(content);
            link.download = `${name?.split(' ')?.join('-')?.toLowerCase()}-card-name.zip`;
            link.click();
        });
    };

    // const handleDownloadMultipleImages = () => {
    //     // qrCodeRef?.current?.download('webp', `${name}-QR`);
    //     for (const image of [
    //         { type: 'front', value: imageFront },
    //         { type: 'back', value: imageBack },
    //     ]) {
    //         const link = document.createElement('a');
    //         link.href = image.value;
    //         link.download = `${name?.split(' ')?.join('-')?.toLowerCase()}-card-${image.type}.png`;
    //         link.click();
    //     }
    // };

    const generateComponentToImage = async () => {
        const elements = [cardFrontRef.current, cardBackRef.current];
        const canvases = await Promise.all(
            elements.map((el) =>
                html2canvas(el as any, {
                    scale: 3,
                    allowTaint: true,
                    useCORS: true,
                }),
            ),
        );

        canvases.forEach((canvas, index) => {
            const base64image = (canvas as any)?.toDataURL('image/png');
            if (index === 0) setImageFront('data:image/png;' + base64image);
            if (index === 1) setImageBack('data:image/png;' + base64image);
        });
    };

    return (
        <ReactPortalComponent>
            <Modal
                className='md:w-[400px] md:h-max w-full p-6 md:block flex flex-col'
                title=' '
                show={show}
                onClose={onClose}
            >
                <div className='flex flex-col items-center gap-6'>
                    <Image
                        src='https://www.rumah123.com/asset-core/images/assets/logo_rumah123.svg'
                        alt='Logo Rumah123'
                        title='Logo Rumah123'
                        decoding='async'
                        loading='lazy'
                        draggable='false'
                        width={117}
                        height={24}
                    />
                    {isSelf ? (
                        <div className='identity-card mt-2'>
                            <div className='identity-card-inner'>
                                <div className='identity-card-front'>
                                    {imageFront ? (
                                        <img
                                            src={imageFront}
                                            alt='Kartu Nama Bagian Depan'
                                            className='w-full h-full object-contain bg-white'
                                        />
                                    ) : (
                                        <div className='flex items-center gap-2'>
                                            <AnimatedSpinner />
                                            <div className='text-accent'>
                                                Generating Card...
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className='identity-card-back'>
                                    {imageBack ? (
                                        <img
                                            src={imageBack}
                                            alt='Kartu Nama Bagian Belakang'
                                            className='w-full h-full object-contain bg-white'
                                        />
                                    ) : (
                                        <div className='flex items-center gap-2'>
                                            <AnimatedSpinner />
                                            <div className='text-accent'>
                                                Generating Card...
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className='w-full h-0 overflow-hidden'>
                                <CardFront
                                    ref={cardFrontRef}
                                    className='rounded-2xl'
                                    profilePicture={profilePicture}
                                    isValidImage={isValidImage}
                                />
                                <CardBack
                                    ref={cardBackRef}
                                    className='rounded-2xl'
                                />
                            </div>
                        </div>
                    ) : (
                        <div className='flex flex-col items-center gap-2'>
                            <QRCode
                                ref={qrCodeRef}
                                value={fullUrl}
                                size={240}
                                enableCORS
                                logoImage={`/portal-api/image/name-alias/?src=${profilePicture}&name=${name}`}
                                logoWidth={60}
                                logoHeight={60}
                                logoPadding={8}
                            />
                        </div>
                    )}
                    <div className='text-lg font-bold'>{name}</div>
                    <div className='relative flex items-center justify-center gap-4'>
                        <button
                            role='button'
                            className={`cursor-pointer flex gap-2 items-center justify-center w-max h-12 rounded-lg px-4 ${buttonActionStyle[theme]}`}
                            onClick={() => {
                                if (isSelf) {
                                    handleDownloadZip();
                                } else {
                                    handleDownload();
                                }
                            }}
                        >
                            <DownloadIcon /> Download{isSelf && ' Kartu Nama'}
                        </button>
                        <button
                            role='button'
                            className={`cursor-pointer flex items-center justify-center w-12 h-12 rounded-lg ${buttonActionStyle[theme]}`}
                            onClick={onShare}
                        >
                            <ShareIcon />
                        </button>
                    </div>
                </div>
            </Modal>
        </ReactPortalComponent>
    );
};

export default ModalQRCodeAgent;
